'use client'

import type { BuiltInProviderType } from 'next-auth/providers'
import type { ClientSafeProvider, LiteralUnion } from 'next-auth/react'
import { signIn } from 'next-auth/react'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import configuration from '~/configuration'
import type { IFormAction } from '~/core/@types/global'
import { PUBLIC_APP_NAME, PUBLIC_LANDING_PAGE_URL } from '~/core/constants/env'
import { Button } from '~/core/ui/Button'
import { Container } from '~/core/ui/Container'
import { DynamicImportForm } from '~/core/ui/DynamicImportForm'
import { FormControlItem } from '~/core/ui/FormControlItem'
import { TypographyH2 } from '~/core/ui/Heading'
import { Input } from '~/core/ui/Input'
import type { ISocialProps } from '~/core/ui/SocialButton'
import { SocialButton } from '~/core/ui/SocialButton'
import { TypographyText } from '~/core/ui/Text'
import { currentTimeZone } from '~/core/utilities/common'

import schemaLoginForm from '~/lib/features/login/schema/login-form'
import type { ILoginForm } from '~/lib/features/login/types'
import { useTrackingUTM } from '~/lib/hooks/use-tracking-utm'

import DropdownLanguage from '../DropdownLanguage'

interface LoginViewProps {
  queryErrorMessage?: string
  providers?: Record<LiteralUnion<BuiltInProviderType, string>, ClientSafeProvider> | null
  onFinish: (data: ILoginForm, formAction: IFormAction) => Promise<void>
  loginSSO: () => void
  isLoadingEmail: boolean
  isLoadingSSO: boolean
}

function LoginView({
  queryErrorMessage,
  providers,
  onFinish,
  isLoadingEmail,
  loginSSO,
  isLoadingSSO
}: LoginViewProps) {
  const { t } = useTranslation()
  const { url, isHasParam } = useTrackingUTM()
  const [isLoadingSocial, setLoadingSocial] = useState<any>()
  const [errorMessage, setErrorMessage] = useState(queryErrorMessage)

  return (
    <div className="flex h-full flex-col justify-between">
      <div className="tablet:mb-0 mb-12 flex items-center justify-center space-x-1 pt-6 text-center">
        {/* <TypographyText className="text-base text-gray-700 dark:text-gray-300">
          <Trans i18nKey={'auth:loginHaveAccount'} />
        </TypographyText>
        <Link href={configuration.path.register}>
          <TypographyText className="text-base font-medium text-primary-400 hover:underline dark:text-primary-400">
            <Trans i18nKey={'auth:loginHaveAccountButton'} />
          </TypographyText>
        </Link> */}
        &nbsp;
      </div>

      <Container overrideClass="mx-0 w-full tablet:max-w-[448px] tablet:mx-auto px-6 desktop:px-0">
        <div className="flex flex-col">
          <div className="flex shrink-0 grow flex-col items-center justify-center">
            <div className="flex w-full flex-col items-center">
              {configuration.production === false ? (
                <Image width={64} height={64} alt="logo" src="/img/logo/logo-primary.png" />
              ) : (
                <a
                  href={`${PUBLIC_LANDING_PAGE_URL}/?utm_campaign=${(
                    PUBLIC_APP_NAME || ''
                  )?.toUpperCase()}&utm_source=app-${(
                    PUBLIC_APP_NAME || ''
                  )?.toLowerCase()}&utm_medium=logo&utm_content=landing-explore`}
                  target="_blank">
                  <Image width={64} height={64} alt="logo" src="/img/logo/logo-primary.png" />
                </a>
              )}

              <div className="desktop:my-10 my-8 text-center">
                <TypographyH2 className="desktop:text-3xl text-center text-2xl text-gray-900 dark:text-gray-300">
                  <Trans i18nKey={'auth:loginWelcome'} />
                </TypographyH2>
                <div className="desktop:mt-3 mt-2">
                  <TypographyText className="text-base text-gray-700 dark:text-gray-300">
                    <Trans i18nKey={'auth:loginWelcomeDescription'} />
                  </TypographyText>
                </div>
              </div>

              <DynamicImportForm
                id="login-form"
                className="tablet:mb-6 tablet:pb-6 desktop:px-2 mb-4 w-full border-b border-gray-100 pb-4"
                schema={schemaLoginForm(t)}
                onSubmit={onFinish}
                noUseSubmit>
                {({ formState, control, setError }) => {
                  // eslint-disable-next-line react-hooks/rules-of-hooks
                  useEffect(() => {
                    if (errorMessage) {
                      setError('email', {
                        type: 'custom',
                        message: errorMessage
                      })
                      setErrorMessage('')
                    }
                  }, [errorMessage])

                  return (
                    <>
                      <div className="mb-4">
                        <Controller
                          control={control}
                          name="email"
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <FormControlItem
                              destructive={formState.errors && !!formState.errors.email}
                              destructiveText={
                                formState.errors && (formState.errors.email?.message as string)
                              }
                              labelRequired
                              label={`${t('auth:loginFormLabelEmail')}`}>
                              <Input
                                onChange={onChange}
                                value={value}
                                destructive={formState.errors && !!formState.errors.email}
                              />
                            </FormControlItem>
                          )}
                        />
                      </div>

                      <Button
                        isDisabled={isLoadingEmail}
                        isLoading={isLoadingEmail}
                        className="w-full"
                        htmlType="submit"
                        label={`${t('auth:loginButtonSubmitWithEmail')}`}
                      />
                    </>
                  )
                }}
              </DynamicImportForm>

              {providers && Object.keys(providers).length ? (
                <div className="desktop:px-2 w-full">
                  {Object.values(providers).map((provider) => (
                    <div key={provider.name} className="mt-2 flex-1 first:mt-0">
                      <SocialButton
                        isLoading={isLoadingSocial?.[provider.id]}
                        className="w-full"
                        onClick={async () => {
                          await setLoadingSocial({
                            [provider.id]: true
                          })
                          return signIn(
                            provider.id,
                            {
                              callbackUrl: isHasParam ? url : undefined
                            },
                            { timeZone: currentTimeZone }
                          )
                        }}
                        size="lg"
                        type={provider.id as unknown as ISocialProps}
                        label={`${t('auth:loginSocialWith', {
                          name: provider.id === 'azure-ad' ? 'Microsoft' : provider.name
                        })}`}
                      />
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="desktop:px-2 mt-2 w-full">
                <div className="mt-2 flex-1 first:mt-0">
                  <SocialButton
                    isLoading={isLoadingSSO}
                    className="w-full"
                    onClick={loginSSO}
                    size="lg"
                    type="sso"
                    label={`${t('auth:loginSocialWith', { name: 'SSO' })}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
      <div className="tablet:mt-4 mb-4 mt-[94px] flex w-full items-center justify-center space-x-8">
        <div>
          <a href={`mailto:${configuration.mailto}`} className="hover:underline">
            <TypographyText className="text-center text-sm font-medium text-gray-700">
              <Trans i18nKey="auth:loginTroubleContact" />
            </TypographyText>
          </a>
        </div>
        <DropdownLanguage />
      </div>
    </div>
  )
}

export default LoginView
